import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'qs';
import { columnOrderMapAdditionalReports } from '../../constants/excelData';
import { reportingOp } from 'store/ducks/Reportings';
import { RootState } from 'store/configureStore';
import AR0101 from 'components/Tables/Reporting/Additionals/AR0101/AR0101';
import AR0102 from 'components/Tables/Reporting/Additionals/AR0102/AR0102';
import AR0103 from 'components/Tables/Reporting/Additionals/AR0103/AR0103';
import ReportData from 'components/Tables/Reporting/Additionals/reportData/reportData';
import ReportFunctions from 'components/Tables/Reporting/Additionals/reportFunctions/reportFunctions';
import ReportTPPs from 'components/Tables/Reporting/Additionals/reportTPPs/reportTPPs';
import ReportICTServices from 'components/Tables/Reporting/Additionals/reportICTServices/reportICTServices';
import ReportICTSystems from 'components/Tables/Reporting/Additionals/reportICTSystems/reportICTSystems';
import RolesAndResponsibilities from 'components/Tables/Reporting/Additionals/Article8/RolesAndResponsibilities';
import RiskSource from 'components/Tables/Reporting/Additionals/Article8/RiskSource';
import RiskExposure from 'components/Tables/Reporting/Additionals/Article8/RiskExposure';
import {
  sheetTitlesAdditional,
  columnHeadersMapAdditional,
} from '../../constants/excelData';
import { generateExcelData } from 'utils/hooks/excelUtils';
import CertificatesByICTService from 'components/Tables/Reporting/Additionals/certificatesByICTServices/certificatesByICTServices';
import CertificatesByFunction from 'components/Tables/Reporting/Additionals/certificatesByFunction/certificatesByFunction';
import CertificatesByICTSystemsByFunctions from 'components/Tables/Reporting/Additionals/certificatesByICTSystemsForFunctions/certificatesByICTSystemsForFunctions';
import CertificatesByICTSystemsByICTServices from 'components/Tables/Reporting/Additionals/certificatesByICTSystemsForICTServices/certificatesByICTSystemsForICTServices';
import EntitiesByFinancialLei from 'components/Tables/Reporting/Additionals/entitiesByFinancialLei/entitiesByFinancialLei';
import RiskExposureFunctionDetails from 'components/Tables/Reporting/Additionals/Article8/RiskExposureFunctionDetails';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import ICTSystemsByFunctions from 'components/Tables/Reporting/Additionals/Article8/ICTSystemsByFunctions';
import ICTSystemsByServices from 'components/Tables/Reporting/Additionals/Article8/ICTSystemsByServices';
import * as XLSX from 'xlsx';
import RiskExposureServiceDetails from 'components/Tables/Reporting/Additionals/Article8/RiskExposureServiceDetails';

export interface ReportModalProps {
  title: string;
  headers: string[];
  tableNumber: string;
}

const ReportModal: React.FC<ReportModalProps> = ({ title, tableNumber }) => {
  const { date } = useSelector((state: RootState) => state.reportings);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const entityName =
    typeof queryParams.entity_name === 'string' ? queryParams.entity_name : '';

  useEffect(() => {
    if (entityName) {
      console.log(entityName, 'entityName');
    }
  }, [entityName]);

  const showModal = () => setOpen(true);
  const handleCancel = () => setOpen(false);

  const fetchDataByTableNumber = async (tableNumber: string, params: any) => {
    switch (tableNumber) {
      case 'Functions with or without ICT Services':
        return await reportingOp.fetchAR0101(params);
      case 'Functions with ICT Services':
        return await reportingOp.fetchAR0102(params);
      case 'ICT Services not linked to Functions':
        return await reportingOp.fetchAR0103(params);
      case 'Information Assets (Data)':
        return await reportingOp.fetchReportData(params);
      case 'Functions':
        return await reportingOp.fetchReportFunctions(params);
      case 'Third Party Service Provider':
        return await reportingOp.fetchReportTPPs(params);
      case 'ICT Services':
        return await reportingOp.fetchReportICTServices(params);
      case 'Certificates of Functions':
        return await reportingOp.fetchCertificatesByFunction(params);
      case 'Certificates of ICT Services':
        return await reportingOp.fetchCertificatesByICTServices(params);
      case 'Certificates of ICT Systems by Functions':
        return await reportingOp.fetchCertificatesByICTSystemFunction(params);
      case 'Certificates of ICT Systems by ICT Services':
        return await reportingOp.fetchCertificatesByICTSystemICTService(params);
      case 'Entities by Financial Lei':
        return await reportingOp.fetchEntitiesByFinancialLei(params);
      case 'Roles and Responsibilities':
        return await reportingOp.getRolesAndResponsibilities(
          params.entity_id,
          params.date,
        );
      case 'Sources of ICT risk':
        return await reportingOp.getICTRiskSource(
          params.entity_id,
          params.date,
        );
      case 'Risk exposure to and from other financial entities':
        return await reportingOp.getICTExposure(params.entity_id, params.date);
      case 'ICT exposure function details':
        return await reportingOp.getICTRiskExposureFunctionDetails(
          params.entity_id,
          params.date,
        );
      case 'ICT exposure service details':
        return await reportingOp.getICTRiskExposureService(
          params.entity_id,
          params.date,
        );
      case 'ICT Systems by function':
        return await article8Op.fetchICTSystemsByFunctions(params);
      case 'ICT Systems by services':
        return await article8Op.fetchICTSystemsByICTServices(params);
      default:
        throw new Error('Invalid tables');
    }
  };

  const handleOk = async () => {
    setLoading(true);
    try {
      const params = { entity_name: queryParams.entity_name, date };

      const fetchedData = await fetchDataByTableNumber(tableNumber, params);
      let excelData: any[] = [];
      if (
        fetchedData?.filtered_data &&
        Array.isArray(fetchedData.filtered_data)
      ) {
        excelData = fetchedData.filtered_data.map((item: any) => ({
          ...item,
          date: fetchedData.date,
          entity_name: fetchedData.entity_name,
        }));
      }

      const { wb, title: excelTitle } = generateExcelData(
        excelData,
        title,
        columnOrderMapAdditionalReports,
        sheetTitlesAdditional,
        columnHeadersMapAdditional,
      );
      XLSX.writeFile(wb, `${excelTitle}.xlsx`);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const renderTableComponent = () => {
    const props = { entityName };
    switch (title) {
      case 'Functions with or without ICT Services':
        return <AR0101 {...props} />;
      case 'Functions with ICT Services':
        return <AR0102 {...props} />;
      case 'ICT Services not linked to Functions':
        return <AR0103 {...props} />;
      case 'Information Assets (Data)':
        return <ReportData {...props} />;
      case 'Functions':
        return <ReportFunctions {...props} />;
      case 'Third Party Service Provider':
        return <ReportTPPs {...props} />;
      case 'ICT Services':
        return <ReportICTServices {...props} />;
      case 'ICT Systems':
        return <ReportICTSystems {...props} />;
      case 'Certificates of Functions':
        return <CertificatesByFunction {...props} />;
      case 'Certificates of ICT Services':
        return <CertificatesByICTService {...props} />;
      case 'Certificates of ICT Systems by Functions':
        return <CertificatesByICTSystemsByFunctions {...props} />;
      case 'Certificates of ICT Systems by ICT Services':
        return <CertificatesByICTSystemsByICTServices {...props} />;
      case 'Entities by Financial Lei':
        return <EntitiesByFinancialLei {...props} />;
      case 'Roles and Responsibilities':
        return <RolesAndResponsibilities {...props} />;
      case 'Sources of ICT risk':
        return <RiskSource {...props} />;
      case 'Risk exposure to and from other financial entities':
        return <RiskExposure {...props} />;
      case 'ICT exposure function details':
        return <RiskExposureFunctionDetails {...props} />;
      case 'ICT exposure service details':
        return <RiskExposureServiceDetails {...props} />;
      case 'ICT Systems by function':
        return <ICTSystemsByFunctions {...props} />;
      case 'ICT Systems by services':
        return <ICTSystemsByServices {...props} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Button type="primary" onClick={showModal}>
        {title}
      </Button>
      <Modal
        open={open}
        centered
        width={1500}
        title={title}
        onOk={handleOk}
        onCancel={handleCancel}
        wrapClassName="reporting-modal"
        footer={[
          <Button key="back" onClick={handleCancel}>
            Return
          </Button>,
        ]}
      >
        {renderTableComponent()}
      </Modal>
    </>
  );
};

export default ReportModal;
